/**
 * SlickGrid Core Utility Functions
 *
 * Pure utility functions for SlickGrid operations that can be tested independently.
 * These handle row IDs, tree grid mapping, pinned columns, and other core operations.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Row ID type - supports string, number, or ObjectId-like objects */
export type RowId = string | number | { toString(): string };

/** Parent ID type - can be null for root level items */
export type ParentId = string | number | null;

/** Base type for SlickGrid row data */
export interface GridRowBase {
  id?: RowId;
  _id?: RowId;
  isRowDisabled?: boolean;
  __isDummy?: boolean;
  __hostRowId?: RowId;
  __parentId?: string;
  disableBorder?: boolean;
  __hasChildren?: boolean;
  __collapsed?: boolean;
  parentId?: RowId | null;
  indentLevel?: number;
  treeLevel?: number;
  hasChild?: boolean;
  isExpanded?: boolean;
  __group?: boolean;
  __groupTotals?: boolean;
}

/** Row metadata for cell spans */
export interface RowMetadata {
  columns?: Record<number, { rowspan: number }>;
  cssClasses?: string;
}

/** Result of row span calculation */
export interface RowSpanResult<T extends GridRowBase> {
  newDataset: T[];
  metadata: Record<string, RowMetadata>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Special column IDs that should always be pinned first */
export const BASE_PINNED: readonly string[] = ["_checkbox_selector", "_move"] as const;

/** Default page size for pagination */
export const DEFAULT_PAGE_SIZE = 10;

/** Number of rows to fetch at a time for infinite scroll */
export const FETCH_SIZE = 30;

// ============================================================================
// DYNAMIC KEY ACCESS
// ============================================================================

/**
 * Read a caller-supplied key (`idMapping`, `hasChildMapping`, …) off a row.
 * Row types are interfaces without an index signature, so `row[key]` would need a cast that claims
 * the row has one; `Reflect.get` reads the same property and keeps the result `unknown`.
 */
const readDynamicKey = (source: object, key: string): unknown => Reflect.get(source, key);

/**
 * Narrow an untyped property value to a row id.
 *
 * `RowId`'s third arm is structural (`{ toString(): string }`), so EVERY non-nullish value carries
 * it — booleans, bigints, symbols and functions included; only `null`/`undefined` cannot be a row
 * id. Falsy mapped values still fall through to `_id` via the `||` chain in `ensureIds`, which is
 * exactly what the truthiness test this replaced did.
 */
const toRowId = (value: unknown): RowId | undefined => (value === null || value === undefined ? undefined : value);

// ============================================================================
// ROW ID HANDLING
// ============================================================================

/**
 * Generate a unique row ID
 */
export const generateRowId = (index: number): string => {
  return `auto_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Normalize a row ID to string format
 */
export const normalizeRowId = (id: RowId | null | undefined): string => {
  if (id === null || id === undefined) return "";
  if (typeof id === "object" && "toString" in id) {
    return id.toString();
  }
  return String(id);
};

/**
 * Ensure all rows have IDs, generating them if necessary
 */
export const ensureIds = <T extends GridRowBase>(arr: T[] | undefined, idMapping?: string): T[] => {
  // `Array.isArray` also guards against a non-array reaching this from an untyped caller.
  // BEHAVIOUR NOTE: such a value is now dropped for `[]` instead of being handed back as if it were
  // a row array (the old `arr ?? []`), which only postponed the crash to the caller's first `.map`.
  if (!arr || !Array.isArray(arr)) return [];

  return arr.map((data, index) => {
    if (data.id) return data;

    const mappedId = idMapping ? toRowId(readDynamicKey(data, idMapping)) : undefined;
    // `||` (not `??`): an empty/zero mapped value falls through to `_id`, as before.
    const id = mappedId || data._id || generateRowId(index);

    return { ...data, id };
  });
};

/**
 * Check if two row IDs are equal
 */
export const areRowIdsEqual = (id1: RowId | null | undefined, id2: RowId | null | undefined): boolean => {
  return normalizeRowId(id1) === normalizeRowId(id2);
};

// ============================================================================
// TREE GRID HANDLING
// ============================================================================

/**
 * Map tree grid flags (__hasChildren, __collapsed) from a custom hasChild mapping
 */
export const mapTreeGridFlags = <T extends GridRowBase>(arr: T[] | undefined, hasChildMapping: string): T[] => {
  if (!arr) return [];
  return arr.map((data) => {
    const hasChildren = Boolean(readDynamicKey(data, hasChildMapping));
    return { ...data, __hasChildren: hasChildren, __collapsed: hasChildren ? true : undefined };
  });
};

/**
 * Get the tree level for a row (0 for root level)
 */
export const getTreeLevel = <T extends GridRowBase>(row: T): number => {
  return row.treeLevel ?? row.indentLevel ?? 0;
};

/**
 * Check if a row has children
 */
export const hasChildren = <T extends GridRowBase>(row: T): boolean => {
  return row.__hasChildren === true || row.hasChild === true;
};

/**
 * Check if a row is collapsed
 */
export const isCollapsed = <T extends GridRowBase>(row: T): boolean => {
  return row.__collapsed === true;
};

/**
 * Check if a row is expanded
 */
export const isExpanded = <T extends GridRowBase>(row: T): boolean => {
  return row.isExpanded === true || row.__collapsed === false;
};

/**
 * Check if a row is a dummy row (for row spanning)
 */
export const isDummyRow = <T extends GridRowBase>(row: T): boolean => {
  return row.__isDummy === true;
};

/**
 * Check if a row is a group row
 */
export const isGroupRow = <T extends GridRowBase>(row: T): boolean => {
  return row.__group === true;
};

/**
 * Check if a row is a group totals row
 */
export const isGroupTotalsRow = <T extends GridRowBase>(row: T): boolean => {
  return row.__groupTotals === true;
};

// ============================================================================
// PINNED COLUMNS
// ============================================================================

/**
 * Add base pinned columns to an array, ensuring no duplicates
 */
export const withBasePinned = (arr: string[]): string[] => {
  const rest = arr.filter((id) => !BASE_PINNED.includes(id));
  return [...BASE_PINNED, ...Array.from(new Set(rest))];
};

/**
 * Check if a column ID is a base pinned column
 */
export const isBasePinnedColumn = (columnId: string): boolean => {
  return BASE_PINNED.includes(columnId);
};

/**
 * Get the frozen column index based on pinned columns count
 */
export const getFrozenColumnIndex = (pinnedCount: number): number => {
  return pinnedCount > 0 ? pinnedCount - 1 : -1;
};

// ============================================================================
// VALUE FORMATTING
// ============================================================================

interface ObjectWithName {
  name?: string;
  label?: string;
  toString?: () => string;
}

/**
 * Format an object value for display
 */
export const formatObjectValue = (value: ObjectWithName): string => {
  if (value.name) return String(value.name);
  if (value.label) return String(value.label);

  if (value.toString && value.toString !== Object.prototype.toString) {
    return value.toString();
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "[Object]";
  }
};

/**
 * Format a group value for display (null/empty shows as "—")
 */
export const formatGroupValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return formatObjectValue(value as ObjectWithName);
  return String(value);
};

/**
 * Sanitize HTML by extracting text content
 */
export const sanitizeHtml = (input: string): string => {
  if (typeof document === "undefined") {
    // Server-side fallback
    return input.replace(/<[^>]*>/g, "");
  }
  const doc = new DOMParser().parseFromString(input || "", "text/html");
  return doc.body.textContent || "";
};

// ============================================================================
// ROW ELEMENT CALCULATIONS
// ============================================================================

/**
 * Get a row element from the even/odd row arrays based on index
 */
export const getRowElement = (
  rowIndex: number,
  evenRowsArray: NodeListOf<Element> | Element[],
  oddRowsArray: NodeListOf<Element> | Element[],
): Element | undefined => {
  if (rowIndex < 0) return undefined;
  return rowIndex % 2 === 0 ? evenRowsArray[rowIndex / 2] : oddRowsArray[Math.floor(rowIndex / 2)];
};

/**
 * Calculate the midpoint Y position of an element
 */
export const calculateMidpointY = (element: Element): number => {
  const rect = element.getBoundingClientRect();
  return rect.top + rect.height / 2;
};

/**
 * Check if a position is within tolerance of a midpoint
 */
export const isInMidpointRange = (currentTop: number, midpointY: number, tolerance: number): boolean => {
  return currentTop >= midpointY - tolerance && currentTop <= midpointY + tolerance;
};

// ============================================================================
// DRAG AND DROP VALIDATION
// ============================================================================

/**
 * Check if a drag operation should be skipped
 */
export const shouldSkipDrag = (
  dataRow: number | null,
  insertBefore: number | null,
  droppedRowIndex: number,
): boolean => {
  if (dataRow === null) return true;
  if (insertBefore !== null && dataRow === insertBefore - 1) return true;
  if (droppedRowIndex === -1) return true;
  return false;
};

/**
 * Calculate new parent ID for a dropped row
 */
export const calculateNewParentId = <T extends GridRowBase>(
  currentTop: number,
  movedRowTop: number,
  droppedRowTop: number,
  droppedRowHeight: number,
  droppedRowData: T | null,
  tolerance: number = 10,
): RowId | null => {
  if (!droppedRowData) return null;

  const midpointY = droppedRowTop + droppedRowHeight / 2;

  if (movedRowTop < droppedRowTop) return null;

  if (isInMidpointRange(currentTop, midpointY, tolerance)) {
    return droppedRowData._id ?? null;
  }

  return null;
};

// ============================================================================
// ROW SPAN CALCULATIONS
// ============================================================================

/**
 * Create a main row with proper ID and border settings
 */
export const createMainRow = <T extends GridRowBase>(item: T, index: number | undefined, requiredRows: number): T => {
  const rowId = item.id ?? `row_${index ?? crypto.randomUUID()}`;
  return {
    ...item,
    id: rowId,
    disableBorder: requiredRows > 1,
  };
};

/**
 * Create dummy rows for row spanning
 *
 * A dummy carries only the span/tree bookkeeping, never the source row's own fields, so it is a
 * `GridRowBase` placeholder — returning `T[]` would promise callers data these rows do not have.
 */
export const createDummyRows = (
  item: GridRowBase,
  mainRowId: RowId,
  requiredRows: number,
  isParentCollapsed: boolean = false,
): GridRowBase[] => {
  const dummies: GridRowBase[] = [];

  for (let iIdx = 1; iIdx < requiredRows; iIdx++) {
    dummies.push({
      id: `${mainRowId}_dummy_${iIdx}`,
      __hostRowId: mainRowId,
      __isDummy: true,
      __parentId: String(item.parentId ?? ""),
      parentId: item.parentId,
      treeLevel: item.treeLevel,
      indentLevel: item.indentLevel,
      hasChild: false,
      __hasChildren: false,
      __collapsed: isParentCollapsed,
      disableBorder: iIdx < requiredRows - 1,
    });
  }

  return dummies;
};

/**
 * Build metadata for row spanning
 */
export const buildMetadataForRow = (columnCount: number, requiredRows: number): RowMetadata => {
  const columnsMeta: Record<number, { rowspan: number }> = {};
  for (let colIdx = 0; colIdx < columnCount; colIdx++) {
    columnsMeta[colIdx] = { rowspan: requiredRows };
  }
  return { columns: columnsMeta };
};

// ============================================================================
// FIELD NAME UTILITIES
// ============================================================================

/**
 * Remove the last numeric index from a field name (e.g., "field_1" -> "field")
 */
export const removeLastIndex = (field: string): string => {
  return field.replace(/_\d+$/, "") || field;
};

/**
 * Extract the base field name without any suffixes
 */
export const getBaseFieldName = (field: string): string => {
  if (!field) return field;
  // Remove common suffixes like _start, _end, _1, etc.
  return field.replace(/_(start|end|\d+)$/, "");
};

// ============================================================================
// PAGINATION CALCULATIONS
// ============================================================================

/**
 * Calculate total pages from item count and page size
 */
export const calculateTotalPages = (totalItems: number, pageSize: number): number => {
  if (pageSize <= 0) return 1;
  const pages = Math.ceil(totalItems / pageSize);
  return pages === 0 ? 1 : pages;
};

/**
 * Calculate skip value for pagination
 */
export const calculateSkip = (currentPage: number, pageSize: number): number => {
  return (currentPage - 1) * pageSize;
};

/**
 * Calculate the range of items being displayed
 */
export const calculateDisplayRange = (
  currentPage: number,
  pageSize: number,
  totalItems: number,
): { start: number; end: number } => {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  return { start: Math.max(1, start), end: Math.max(start, end) };
};

/**
 * Validate page number and return a valid page
 */
export const validatePageNumber = (page: number, totalPages: number): number => {
  if (page < 1) return 1;
  if (page > totalPages) return totalPages;
  return page;
};

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

/**
 * Check if an array is empty or undefined
 */
export const isArrayEmpty = (arr: unknown[] | undefined | null): boolean => {
  return !arr || arr.length === 0;
};

/**
 * Safely get the length of an array
 */
export const safeArrayLength = (arr: unknown[] | undefined | null): number => {
  return arr?.length ?? 0;
};

/**
 * Remove duplicates from an array while maintaining order
 */
export function uniqueArray<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Move an item in an array from one index to another
 */
export function moveArrayItem<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex < 0 || fromIndex >= arr.length) return arr;
  if (toIndex < 0 || toIndex >= arr.length) return arr;
  if (fromIndex === toIndex) return arr;

  const result = [...arr];
  const [item] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, item!);
  return result;
}

// ============================================================================
// COMPARISON UTILITIES
// ============================================================================

/**
 * Compare two values for equality (deep comparison for objects)
 */
export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === "object" && typeof b === "object") {
    // Check if both are arrays or both are plain objects
    const aIsArray = Array.isArray(a);
    const bIsArray = Array.isArray(b);
    if (aIsArray !== bIsArray) return false;

    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => deepEqual(readDynamicKey(a, key), readDynamicKey(b, key)));
  }

  return false;
};

// ============================================================================
// DEBOUNCE UTILITY
// ============================================================================

export interface DebouncedFunction<TArgs extends unknown[]> {
  (...args: TArgs): void;
  cancel: () => void;
}

/**
 * Create a debounced version of a function
 */
export const makeDebounce = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay: number,
): DebouncedFunction<TArgs> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };

  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };

  return debounced;
};
